import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Zap, Flame, Trophy, FileText, Bell, TrendingUp, 
  Users, Package, ArrowRight, Star, Shield, Clock,
  DollarSign, MapPin, Truck, CheckCircle, AlertTriangle,
  Download, RefreshCw, Target, Award, Sparkles
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommandCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [hotLoads, setHotLoads] = useState([]);
  const [topLanes, setTopLanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // AI Match state
  const [matchLoadId, setMatchLoadId] = useState('');
  const [matchResults, setMatchResults] = useState(null);
  const [matching, setMatching] = useState(false);
  
  // Rate Con state
  const [rateConLoadId, setRateConLoadId] = useState('');
  const [rateConEmail, setRateConEmail] = useState('');
  const [generating, setGenerating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [notifRes, hotRes, lanesRes] = await Promise.all([
        axios.get(`${API}/notifications/dashboard`),
        axios.get(`${API}/loads/hot`),
        axios.get(`${API}/analytics/lanes?limit=10`)
      ]);
      
      setNotifications(notifRes.data.notifications || []);
      setHotLoads(hotRes.data.hot_loads || []);
      setTopLanes(lanesRes.data.top_lanes || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleAIMatch = async () => {
    if (!matchLoadId) {
      toast.error('Please enter a Load ID');
      return;
    }
    
    setMatching(true);
    try {
      const res = await axios.post(`${API}/ai/auto-match/${matchLoadId}?limit=5`);
      setMatchResults(res.data);
      toast.success(`Found ${res.data.matches.length} matching drivers!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Match failed');
    } finally {
      setMatching(false);
    }
  };

  const handleGenerateRateCon = async () => {
    if (!rateConLoadId || !rateConEmail) {
      toast.error('Please enter Load ID and Driver Email');
      return;
    }
    
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/documents/generate-ratecon/${rateConLoadId}?driver_email=${encodeURIComponent(rateConEmail)}`);
      toast.success('Rate Confirmation generated!');
      
      // Open download
      window.open(`${API}/documents/${res.data.document_id}/download`, '_blank');
      
      setRateConLoadId('');
      setRateConEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleMarkHot = async (loadId) => {
    try {
      await axios.post(`${API}/loads/${loadId}/mark-hot?premium_percent=15&expires_hours=4`);
      toast.success('Load marked as HOT with 15% premium!');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as hot');
    }
  };

  const getNotificationIcon = (icon) => {
    const icons = {
      'flame': Flame,
      'zap': Zap,
      'inbox': Package,
      'alert-triangle': AlertTriangle,
      'dollar-sign': DollarSign,
      'users': Users
    };
    const Icon = icons[icon] || Bell;
    return Icon;
  };

  const getNotificationColor = (type) => {
    const colors = {
      'warning': 'bg-amber-500/20 border-amber-500/30 text-amber-400',
      'action': 'bg-blue-500/20 border-blue-500/30 text-blue-400',
      'info': 'bg-slate-500/20 border-slate-500/30 text-slate-400',
      'success': 'bg-green-500/20 border-green-500/30 text-green-400'
    };
    return colors[type] || colors.info;
  };

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="p-6" data-testid="command-center-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Sparkles className="w-7 h-7 text-purple-500" />
              Command Center
            </h1>
            <p className="text-gray-400 mt-1">AI-powered tools and real-time intelligence</p>
          </div>
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            className="gap-2"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Notifications & Hot Loads */}
          <div className="lg:col-span-2 space-y-6">
            {/* Smart Notifications */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-500" />
                  Smart Notifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-10 h-10 mx-auto mb-3 text-green-500" />
                    <p>All caught up! No pending actions.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notifications.map((notif, idx) => {
                      const Icon = getNotificationIcon(notif.icon);
                      return (
                        <div 
                          key={idx}
                          className={`p-4 rounded-lg border ${getNotificationColor(notif.type)}`}
                        >
                          <div className="flex items-start gap-3">
                            <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="font-medium text-white">{notif.title}</h4>
                              <p className="text-sm opacity-80">{notif.message}</p>
                            </div>
                            <Button size="sm" variant="ghost" className="text-xs">
                              {notif.action} →
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Hot Loads */}
            <Card className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-500" />
                  Hot Loads ({hotLoads.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {hotLoads.length === 0 ? (
                  <div className="text-center py-6 text-gray-400">
                    <p>No hot loads currently. Mark loads as HOT to boost visibility.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {hotLoads.slice(0, 5).map((load, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                        <div>
                          <p className="text-white font-medium">
                            {load.origin?.city || load.pickup_city} → {load.destination?.city || load.delivery_city}
                          </p>
                          <p className="text-sm text-gray-400">{load.equipment_type}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-orange-400 font-bold">+{load.hot_premium_percent}%</p>
                          <p className="text-xs text-gray-500">
                            Expires: {new Date(load.hot_expires_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Lanes */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Top Performing Lanes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topLanes.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">No lane data yet</p>
                ) : (
                  <div className="space-y-2">
                    {topLanes.map((lane, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-yellow-500 text-black' :
                            idx === 1 ? 'bg-gray-400 text-black' :
                            idx === 2 ? 'bg-orange-600 text-white' :
                            'bg-slate-600 text-white'
                          }`}>
                            {idx + 1}
                          </span>
                          <div>
                            <p className="text-white font-medium">{lane.lane}</p>
                            <p className="text-xs text-gray-500">{lane.total_loads} loads</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-mono">${lane.avg_rate.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">avg rate</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Tools */}
          <div className="space-y-6">
            {/* AI Auto-Match */}
            <Card className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-500" />
                  AI Auto-Match
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-400">
                  Instantly find the best drivers for any load using AI scoring.
                </p>
                <div>
                  <Input
                    value={matchLoadId}
                    onChange={(e) => setMatchLoadId(e.target.value)}
                    placeholder="Enter Load ID"
                    className="bg-slate-700/50 border-slate-600 text-white"
                    data-testid="match-load-input"
                  />
                </div>
                <Button 
                  onClick={handleAIMatch}
                  disabled={matching}
                  className="w-full bg-purple-600 hover:bg-purple-500"
                  data-testid="match-btn"
                >
                  {matching ? 'Matching...' : (
                    <><Target className="w-4 h-4 mr-2" /> Find Best Drivers</>
                  )}
                </Button>

                {matchResults && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm text-gray-400">
                      Found {matchResults.matches.length} matches for {matchResults.origin}
                    </p>
                    {matchResults.matches.map((driver, idx) => (
                      <div key={idx} className="p-3 bg-slate-800/50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-white font-medium">{driver.name}</p>
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full font-mono">
                            Score: {driver.match_score}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {driver.match_reasons?.map((reason, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-slate-700 text-gray-400 text-xs rounded">
                              {reason}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Rate Con Generator */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Rate Con Generator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-400">
                  Generate professional Rate Confirmation PDFs instantly.
                </p>
                <div className="space-y-3">
                  <Input
                    value={rateConLoadId}
                    onChange={(e) => setRateConLoadId(e.target.value)}
                    placeholder="Load ID"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                  <Input
                    value={rateConEmail}
                    onChange={(e) => setRateConEmail(e.target.value)}
                    placeholder="Driver Email"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <Button 
                  onClick={handleGenerateRateCon}
                  disabled={generating}
                  className="w-full bg-blue-600 hover:bg-blue-500"
                >
                  {generating ? 'Generating...' : (
                    <><Download className="w-4 h-4 mr-2" /> Generate PDF</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <a href="/admin/premium" className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    <span className="text-white">Revenue Dashboard</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-500" />
                </a>
                <a href="/admin/fleet" className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    <span className="text-white">Manage Fleet</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-500" />
                </a>
                <a href="/admin/documents" className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-500" />
                    <span className="text-white">Documents</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-500" />
                </a>
                <a href="/admin/negotiator" className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-orange-500" />
                    <span className="text-white">AI Negotiator</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-500" />
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CommandCenter;
